import React, { useRef, useState, useEffect } from 'react';
import { SpotlightModuleProps } from '../types/spotlight.types';
import Button from '../../../components/Button';
import { ArrowRight } from 'lucide-react';

const SpotlightModule: React.FC<SpotlightModuleProps> = ({
    media,
    content,
    theme = 'dark',
    className = '',
    align = 'left',
    fadeEffect = true,
    button = true
}) => {
    const textColorClass = theme === 'dark' ? 'text-white' : 'text-navy-900';
    const textOpacityClass = theme === 'dark' ? 'text-white/90' : 'text-navy-900/90';
    const moduleRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (fadeEffect) {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    setIsVisible(entry.isIntersecting);
                });
            }, options);

            const elementToObserve = moduleRef.current;
            if (elementToObserve) {
                observer.observe(elementToObserve);
            }

            return () => observer.disconnect();
        }
    }, [fadeEffect]);

    return (
        <div ref={moduleRef} className={`w-full ${className} ${fadeEffect ? (isVisible ? 'fade-in' : 'fade-out') : ''}`}>
            <div className="module_container">
                <section className="relative w-full overflow-hidden rounded-3xl">
                    {/* Background Media */}
                    <div className="absolute inset-0 w-full h-full">
                        {media.type === 'video' ? (
                            <video
                                autoPlay
                                muted
                                loop
                                playsInline
                                poster={media.posterUrl}
                                className="absolute inset-0 w-full h-full object-cover"
                            >
                                <source src={media.url} type="video/mp4" />
                            </video>
                        ) : (
                            <img
                                src={media.url}
                                alt={media.alt || content.title}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        )}
                        {/* Overlay for text readability */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    </div>

                    {/* Content */}
                    <div className={`relative z-10 px-6 md:pl-10 md:pr-0 py-6 md:py-12 flex ${align === 'center' ? 'items-center justify-center text-center' :
                        align === 'right' ? 'items-end justify-end text-right' :
                            'items-start justify-start text-left'
                        }`}>
                        <div className="max-w-xl">
                            <h2 className={`text-4xl md:text-4xl font-headline mb-4 ${textColorClass}`}>
                                {content.title}
                            </h2>
                            {content.subtitle && (
                                <h3 className={`text-xl md:text-2xl font-headline mb-4 ${textOpacityClass}`}>
                                    {content.subtitle}
                                </h3>
                            )}
                            <p className={`text-lg mb-8 font-body ${textOpacityClass}`}>
                                {content.description}
                            </p>
                            {button && (
                                <Button
                                    to={typeof content.ctaLink === 'function' ? content.ctaLink() : content.ctaLink}
                                    text={content.ctaText}
                                    variant={theme === 'dark' ? 'solid-white' : 'solid-black'}
                                />
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default SpotlightModule; 